import { Button, Card, Checkbox, Form, Input, Message, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InvMovementOrderAPI, InventoryAPI, WarehouseAPI } from "@/apis/material";
import { InputNumber } from "@/components/Numeric";
import { Number } from "@/components/Numeric";
import WarehouseSelect from "@/components/WarehouseSelect";
import { ItemSelect } from "@/components/ItemSelect";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import _ from "lodash";

const TextArea = Input.TextArea;
const LocationSelect = WarehouseSelect.LocationSelect;
const FormItem = Form.Item;

export const InvMovementAdd = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([{ _key: 1 }]);
    const [sameLocation, setSameLocation] = useState(false);
    const [saving, setSaving] = useState(false);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [inventoryCache, setInventoryCache] = useState({});
    const nextKey = useCallback(() => {
        const maxKey = _.maxBy(rows, '_key');
        return (maxKey?._key || 0) + 1;
    }, [rows]);

    useEffect(() => {
        const init = async () => {
            try {
                const warehouses = await WarehouseAPI.actives(true);
                setWarehouseOptions(_.map(_.orderBy(warehouses, ['warehouseName'], ['asc']), (w) => ({
                    value: w.id,
                    label: w.warehouseName
                })));
            } catch (error) {
                Message.error(error.message || "Failed to load data");
            }
        };
        init();
    }, []);

    const loadInventory = useCallback(async (itemId) => {
        if (!itemId) return [];
        try {
            const inventories = await InventoryAPI.itemInventories([itemId]);
            setInventoryCache(prev => ({ ...prev, [itemId]: inventories }));
            return inventories;
        } catch (e) {
            return [];
        }
    }, []);

    const getInventoryForItem = useCallback((itemId) => {
        if (!itemId) return [];
        return inventoryCache[itemId] || [];
    }, [inventoryCache]);

    const getAvailableLocations = useCallback((itemId, warehouseId) => {
        const inventories = getInventoryForItem(itemId);
        return inventories
            .filter(inv => inv.warehouseId === warehouseId && inv.availableQty > 0)
            .map(inv => ({ value: inv.locationId, label: inv.locationName }));
    }, [getInventoryForItem]);

    const getAvailableLots = useCallback((itemId, warehouseId) => {
        const inventories = getInventoryForItem(itemId);
        const filtered = inventories.filter(inv =>
            inv.warehouseId === warehouseId && inv.availableQty > 0
        );
        return _.uniqBy(
            _.filter(_.map(filtered, inv => ({ value: inv.lot, label: inv.lot || "-" })), lot => !_.isEmpty(lot.value) || lot.value === "-"),
            'value'
        );
    }, [getInventoryForItem]);

    const getQtyForItem = useCallback((itemId, warehouseId, locationId, lot) => {
        const inventories = getInventoryForItem(itemId);
        const match = inventories.find(inv =>
            inv.warehouseId === warehouseId
            && (inv.locationId === locationId || (!inv.locationId && !locationId))
            && (inv.lot === lot || (!inv.lot && !lot))
        );
        return match ? match.availableQty : 0;
    }, [getInventoryForItem]);

    const addRow = () => {
        const key = nextKey();
        setRows(prev => [...prev, { _key: key }]);
    };

    const removeRow = (key) => {
        if (rows.length <= 1) {
            Message.warning("At least one row is required");
            return;
        }
        setRows(prev => prev.filter(r => r._key !== key));
    };

    const updateRow = (key, field, value) => {
        setRows(prev => prev.map(r => {
            if (r._key !== key) return r;
            const updated = { ...r, [field]: value };
            // Auto-fill location, lot, qty when item or warehouse changes
            if (field === "itemId") {
                updated.fromWarehouseId = undefined;
                updated.fromLocationId = undefined;
                updated.fromLocationName = undefined;
                updated.fromLot = undefined;
                updated.qty = undefined;
                updated.qtyToMove = undefined;
                if (value) {
                    loadInventory(value);
                }
            }
            if (field === "fromWarehouseId") {
                updated.fromLocationId = undefined;
                updated.fromLocationName = undefined;
                updated.fromLot = undefined;
                updated.qty = undefined;
                updated.qtyToMove = undefined;
            }
            if (field === "fromLocationId") {
                updated.fromLot = undefined;
                updated.qty = undefined;
                updated.qtyToMove = undefined;
            }
            if (field === "fromLot") {
                updated.qty = undefined;
                updated.qtyToMove = undefined;
            }
            return updated;
        }));
    };

    const onItemSelect = (key, item) => {
        const itemId = item?.value;
        updateRow(key, "itemId", itemId);
        if (itemId) {
            updateRow(key, "itemName", item?.itemName || "");
            updateRow(key, "itemDescription", item?.description || "");
            updateRow(key, "itemNo", item?.itemNo || "");
        }
    };

    const onFromWarehouseSelect = (key, warehouseId) => {
        updateRow(key, "fromWarehouseId", warehouseId);
        const warehouse = _.find(warehouseOptions, o => o.value === warehouseId);
        if (warehouse) {
            updateRow(key, "fromWarehouseName", warehouse.label);
        }
    };

    const onFromLocationSelect = (key, locationId, locationName) => {
        updateRow(key, "fromLocationId", locationId);
        updateRow(key, "fromLocationName", locationName || "");
    };

    const onFromLotSelect = (key, lot) => {
        updateRow(key, "fromLot", lot);
        // Auto-fill qty
        const row = rows.find(r => r._key === key);
        if (row) {
            const qty = getQtyForItem(row.itemId, row.fromWarehouseId, row.fromLocationId, lot);
            updateRow(key, "qty", qty);
        }
    };

    const onToWarehouseSelect = (key, warehouseId) => {
        updateRow(key, "toWarehouseId", warehouseId);
        const warehouse = _.find(warehouseOptions, o => o.value === warehouseId);
        if (warehouse) {
            updateRow(key, "toWarehouseName", warehouse.label);
        }
        updateRow(key, "toLocationId", undefined);
        updateRow(key, "toLocationName", undefined);
        // When sameLocation and this is the first row, sync all other rows
        if (sameLocation && key === rows[0]._key) {
            setRows(prev => prev.map(r => {
                if (r._key === key) return r;
                return {
                    ...r,
                    toWarehouseId: warehouseId,
                    toWarehouseName: warehouse?.label || "",
                    toLocationId: undefined,
                    toLocationName: undefined,
                };
            }));
        }
    };

    const onToLocationSelect = (key, locationId, locationName) => {
        updateRow(key, "toLocationId", locationId);
        updateRow(key, "toLocationName", locationName || "");
        // When sameLocation and this is the first row, sync all other rows
        if (sameLocation && key === rows[0]._key) {
            setRows(prev => prev.map(r => {
                if (r._key === key) return r;
                return { ...r, toLocationId: locationId, toLocationName: locationName || "" };
            }));
        }
    };

    const validateRows = () => {
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!row.itemId) {
                Message.warning(`Row ${i + 1}: Please select an item`);
                return false;
            }
            if (!row.fromWarehouseId) {
                Message.warning(`Row ${i + 1}: Please select a source warehouse`);
                return false;
            }
            if (!row.qtyToMove || row.qtyToMove <= 0) {
                Message.warning(`Row ${i + 1}: Please enter qty to move`);
                return false;
            }
            if (!sameLocation && !row.toWarehouseId) {
                Message.warning(`Row ${i + 1}: Please select a destination warehouse`);
                return false;
            }
            if (sameLocation && i === 0 && !rows[0].toWarehouseId) {
                Message.warning("Please select a destination warehouse");
                return false;
            }
        }
        return true;
    };

    const save = async () => {
        try {
            const values = await form.validate();
            if (!validateRows()) return;
            if (saving) return;
            setSaving(true);

            const items = rows.map(row => ({
                itemId: row.itemId,
                itemNo: row.itemNo || "",
                itemName: row.itemName || "",
                itemDescription: row.itemDescription || "",
                fromWarehouseId: row.fromWarehouseId,
                fromWarehouseName: row.fromWarehouseName || "",
                fromLocationId: row.fromLocationId,
                fromLocationName: row.fromLocationName || "",
                fromLot: row.fromLot || "",
                qty: row.qty || 0,
                qtyToMove: row.qtyToMove,
                toWarehouseId: sameLocation ? rows[0].toWarehouseId : row.toWarehouseId,
                toWarehouseName: sameLocation ? (rows[0].toWarehouseName || "") : (row.toWarehouseName || ""),
                toLocationId: sameLocation ? rows[0].toLocationId : row.toLocationId,
                toLocationName: sameLocation ? (rows[0].toLocationName || "") : (row.toLocationName || ""),
            }));

            const payload = {
                sameLocation: sameLocation,
                notes: values.notes,
                items: items,
            };

            const id = await InvMovementOrderAPI.add(payload);
            Message.success("Movement order created successfully");
            navigate(`/workspace/material/inventory_movement/${id}`);
        } catch (error) {
            if (error.errors) {
                // Form validation error - already displayed by Arco Form
                return;
            }
            Message.error(error.message || "Failed to create movement order");
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: (<div style={{ lineHeight: 1.5 }}><div style={{ fontSize: 14, fontWeight: 600 }}>Item Name</div><div style={{ fontSize: 12, color: '#86909c' }}>Description</div></div>),
            width: 280,
            render: (col, row) => (
                <ItemSelect
                    value={row.itemId}
                    onSelectItem={(item) => onItemSelect(row._key, item)}
                    style={{ width: "100%" }} />
            ),
        },
        {
            title: "Warehouse",
            width: 160,
            render: (col, row) => (
                <Select
                    style={{ width: "100%" }}
                    showSearch
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    value={row.fromWarehouseId}
                    options={warehouseOptions}
                    onChange={(val) => onFromWarehouseSelect(row._key, val)}
                />
            ),
        },
        {
            title: "Location",
            width: 140,
            render: (col, row) => {
                const locations = getAvailableLocations(row.itemId, row.fromWarehouseId);
                return (
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        filterOption={(inputValue, option) =>
                            option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        value={row.fromLocationId}
                        options={locations}
                        onChange={(val) => {
                            const option = locations.find(o => o.value === val);
                            onFromLocationSelect(row._key, val, option?.label);
                        }}
                    />
                );
            },
        },
        {
            title: "Lot#",
            width: 120,
            render: (col, row) => {
                const lots = getAvailableLots(row.itemId, row.fromWarehouseId);
                return (
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        filterOption={(inputValue, option) =>
                            option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        value={row.fromLot}
                        options={lots}
                        onChange={(val) => onFromLotSelect(row._key, val)}
                    />
                );
            },
        },
        {
            title: "Qty",
            width: 100,
            render: (col, row) => (
                <span>{row.qty != null ? <Number value={row.qty} /> : "-"}</span>
            ),
        },
        {
            title: "Qty To Move",
            width: 140,
            render: (col, row) => (
                <InputNumber
                    style={{ width: "100%" }}
                    min={0.0001}
                    max={row.qty || 0}
                    precision={5}
                    value={row.qtyToMove}
                    onChange={(val) => updateRow(row._key, "qtyToMove", val)}
                />
            ),
        },
        {
            title: "Warehouse",
            width: 160,
            render: (col, row) => {
                if (sameLocation && row._key !== rows[0]._key) {
                    return <span>{rows[0]?.toWarehouseName || "-"}</span>;
                }
                return (
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        filterOption={(inputValue, option) =>
                            option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        value={row.toWarehouseId}
                        options={warehouseOptions}
                        onChange={(val) => onToWarehouseSelect(row._key, val)}
                    />
                );
            },
        },
        {
            title: "Location",
            width: 140,
            render: (col, row) => {
                if (sameLocation && row._key !== rows[0]._key) {
                    return <span>{rows[0]?.toLocationName || "-"}</span>;
                }
                return (
                    <LocationSelect
                        style={{ width: "100%" }}
                        warehouseId={row.toWarehouseId}
                        onlyActive={false}
                        value={row.toLocationId}
                        onChange={(val, label) => onToLocationSelect(row._key, val, label)}
                    />
                );
            },
        },
        {
            title: "",
            width: 50,
            align: "center",
            render: (col, row) => (
                <Button type="text" icon={<IconDelete />} status="danger" onClick={() => removeRow(row._key)} />
            ),
        },
    ];

    // Group columns under "Move From" and "Move To"
    const groupedColumns = [
        {
            title: "Move From",
            children: columns.slice(0, 6),
        },
        {
            title: (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <span>Move To</span>
                    <Checkbox checked={sameLocation} onChange={(checked) => {
                        setSameLocation(checked);
                        if (checked) {
                            // Set all other rows' To to match the first row's To
                            const firstRow = rows[0];
                            if (firstRow?.toWarehouseId) {
                                setRows(prev => prev.map(r => ({
                                    ...r,
                                    toWarehouseId: firstRow.toWarehouseId,
                                    toWarehouseName: firstRow.toWarehouseName || "",
                                    toLocationId: firstRow.toLocationId,
                                    toLocationName: firstRow.toLocationName || "",
                                })));
                            }
                        } else {
                            // Clear per-row to fields
                            setRows(prev => prev.map(r => ({
                                ...r,
                                toWarehouseId: undefined,
                                toWarehouseName: undefined,
                                toLocationId: undefined,
                                toLocationName: undefined,
                            })));
                        }
                    }}>Same Location</Checkbox>
                </div>
            ),
            children: columns.slice(6, 8),
        },
        columns[8],
    ];

    return (
        <Card title="New Inventory Movement" extra={
            <Space>
                <Button type="primary" loading={saving} onClick={save}>Save</Button>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} layout="vertical">
                <FormItem
                    label="Notes"
                    field="notes"
                    rules={[{ required: true, message: "Please enter notes" }]}
                >
                    <TextArea
                        placeholder="Enter notes..."
                    />
                </FormItem>
            </Form>
            <Table
                rowKey="_key"
                columns={groupedColumns}
                data={rows}
                pagination={false}
                footer={() => (
                    <Button type="outline" size="small" shape="round" status="success"
                        icon={<IconPlus />} onClick={addRow}>
                        Add Row
                    </Button>
                )}
            />
        </Card>
    );
};
